// frontend/src/components/StandingCard.js
//
// The first thing a signed-in user should see: a real number, before any
// onboarding step is done. The free tools answer in seconds; the paid product
// used to show a setup checklist first. This closes that gap.
//
// All arithmetic comes from the server (app/services/standing.py) so the maths
// has one home. Do not reimplement the closed forms here.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { trackEvent } from '../services/analytics';
import './StandingCard.css';

// Airbnb badges and Google targets are different worlds. Mixing them told an
// estate agent it needed 29 reviews to reach 4.8 "Superhost", with a quarterly
// assessment countdown. Keep these lists apart.
const AIRBNB_TARGETS = [
  { value: 4.8, label: '4.8 - Airbnb Superhost' },
  { value: 4.9, label: '4.9 - Guest Favorite' },
  { value: 4.7, label: '4.7' },
  { value: 4.5, label: '4.5' },
];

const GOOGLE_TARGETS = [
  { value: 4.5, label: '4.5 - competitive on Google' },
  { value: 4.7, label: '4.7 - top of most local packs' },
  { value: 4.3, label: '4.3' },
  { value: 4.0, label: '4.0 - the filter most customers use' },
];

const HOSPITALITY = 'hospitality';

function fmt(n, dp) {
  return Number(n).toFixed(dp);
}

function plural(n, one, many) {
  return n === 1 ? one : many;
}

function Result({ data, dp }) {
  // The server names which standing is primary. Never infer it from the number:
  // that is how a Google business ended up being shown the 4.8 Superhost figure.
  const key = data.primary || (data.standings.target ? 'target' : Object.keys(data.standings)[0]);
  const st = data.standings[key] || data.standings[Object.keys(data.standings)[0]];
  if (!st) return null;

  // Present only when the server judged this an Airbnb business; a local business
  // has no quarterly assessment to count down to.
  const showAssessment = Boolean(data.next_assessment && data.days_until_assessment != null);

  return (
    <>
      {st.meets ? (
        <p className="standing-headline">
          You are above {fmt(st.threshold, 1)}. You can absorb{' '}
          <span className="standing-number">{st.buffer_one_stars}</span>{' '}
          more 1-star {plural(st.buffer_one_stars, 'review', 'reviews')} before you drop below it.
        </p>
      ) : (
        <p className="standing-headline standing-below">
          You need <span className="standing-number">{st.five_stars_needed}</span>{' '}
          consecutive 5-star {plural(st.five_stars_needed, 'review', 'reviews')} to reach{' '}
          {fmt(st.threshold, 1)}.
        </p>
      )}
      <p className="standing-meta">
        Currently {fmt(data.mean, dp)} across {data.reviews}{' '}
        {plural(data.reviews, 'review', 'reviews')}.
        {showAssessment && (
          <>
            {' '}The next Superhost assessment is in{' '}
            <strong>{data.days_until_assessment}{' '}
            {plural(data.days_until_assessment, 'day', 'days')}</strong>.
          </>
        )}
      </p>
    </>
  );
}

export default function StandingCard() {
  const [mine, setMine] = useState(null);       // standing from imported reviews
  const [typed, setTyped] = useState(null);     // standing from typed numbers
  const [loading, setLoading] = useState(true);
  // The endpoint ships with the API, which deploys separately from this bundle.
  // Until it exists, hide the card rather than showing a broken one.
  const [available, setAvailable] = useState(true);
  const [rating, setRating] = useState('');
  const [reviews, setReviews] = useState('');
  // Null until /standing/mine tells us what this business is. The target list and
  // default depend on it - a Google business must never be offered 4.8 "Superhost".
  const [category, setCategory] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.request('/standing/mine');
        if (cancelled || !data) return;
        const cat = data.business_category || 'local_business';
        setCategory(cat);
        setTarget(cat === HOSPITALITY ? 4.8 : 4.5);
        if (data.has_reviews) setMine(data);
      } catch (err) {
        // A throw here means the endpoint is not reachable (not yet deployed, or
        // auth lapsed). Hide the card entirely rather than offering a form whose
        // submit would fail.
        if (!cancelled) setAvailable(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const compute = useCallback(async () => {
    const r = parseFloat(rating);
    const n = parseInt(reviews, 10);
    if (target == null) return;
    if (!(r >= 1 && r <= 5) || !(n >= 1)) {
      setError('Enter a rating between 1 and 5, and how many reviews you have.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const data = await api.request('/standing/quick', {
        method: 'POST',
        body: JSON.stringify({ rating: r, reviews: n, threshold: target }),
      });
      setTyped(data);
      trackEvent('dashboard_standing_computed', { target, meets: !!data.standings?.target?.meets });
    } catch (err) {
      setError(err.message || 'Could not work that out. Try again.');
    } finally {
      setBusy(false);
    }
  }, [rating, reviews, target]);

  if (loading || !available) return null;

  // Real reviews imported — show the standing computed from them.
  if (mine && !typed) {
    return (
      <div className="standing-card">
        <h2>Where you stand</h2>
        <p className="standing-sub">
          From the {mine.reviews} {plural(mine.reviews, 'review', 'reviews')} imported for{' '}
          {mine.business_name}.
        </p>
        <Result data={mine} dp={2} />
      </div>
    );
  }

  // Nothing imported yet — ask for two numbers and answer immediately.
  return (
    <div className="standing-card">
      <h2>Where you stand</h2>
      <p className="standing-sub">
        Two numbers and you get the exact answer. No setup needed — import your reviews later
        and this fills in automatically.
      </p>

      <div className="standing-inputs">
        <div>
          <label htmlFor="standing-rating">Current rating</label>
          <input
            id="standing-rating"
            type="number"
            inputMode="decimal"
            min="1"
            max="5"
            step="0.01"
            placeholder="4.76"
            value={rating}
            onChange={(e) => setRating(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="standing-reviews">Number of reviews</label>
          <input
            id="standing-reviews"
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            placeholder="38"
            value={reviews}
            onChange={(e) => setReviews(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="standing-target">Target</label>
          <select
            id="standing-target"
            value={target}
            onChange={(e) => setTarget(parseFloat(e.target.value))}
          >
            {(category === HOSPITALITY ? AIRBNB_TARGETS : GOOGLE_TARGETS).map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      <button className="standing-btn" onClick={compute} disabled={busy}>
        {busy ? 'Working it out…' : 'Show me where I stand'}
      </button>

      {error && <p className="standing-error">{error}</p>}

      {typed && (
        <div style={{ marginTop: 20 }}>
          <Result data={typed} dp={2} />
          {typed.caveat && <p className="standing-caveat">{typed.caveat}</p>}
          <button className="standing-again" onClick={() => setTyped(null)}>
            Try different numbers
          </button>
        </div>
      )}
    </div>
  );
}
